/**
 * One page per animal, wherever you come from: Overview (what matters now and what's next),
 * History (every fact with its source), Records (each domain, shown when it has something), Family.
 * On phones the main actions stay within thumb reach at the bottom while you scroll.
 */
import { Suspense, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ENTITY_FIELDS, type AnimalDetail, type EventTypeDef } from "../../../contract";
import { formatAge, formatDate, useFormat } from "../../lib/format";
import { t, useLanguage } from "../../lib/i18n";
import { useApi } from "../../lib/query";
import { useCan, useRanchToday } from "../../lib/session";
import { openEntity, openRecord } from "../../forms/EventForm";
import { animalActions, animalSections, eventTypesFor, registry } from "../../registry";
import { Spinner, Badges, Button, Card, Chip, Empty, ErrorNote, Facts, Icon, List, Loading, Menu, PageHeader, Row, Section, Tabs, TagLabel, cx, useFromHere, useIsWide } from "../../ui/kit";
import { useCaptureSubject, useOpenComposer } from "../Composer";
import { DataList, classLabel } from "../describe";
import { Page } from "../Layout";
import { Timeline } from "../Timeline";
import { NotFound } from "./NotFound";
import { TaskRow } from "./Work";

export function AnimalPage() {
  useLanguage();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "overview";
  const { data: a, isLoading, error, refetch } = useApi<AnimalDetail>(`/animals/${id}`);
  useCaptureSubject(a ? { subject: { kind: "animal", id: a.id, label: a.label } } : null);
  const openComposer = useOpenComposer();
  const can = useCan();
  const wide = useIsWide();
  const navigate = useNavigate();
  const today = useRanchToday();
  if (isLoading) return <Loading />;
  if (error || !a) return (error as { status?: number } | null)?.status === 404 ? <NotFound title={t("We couldn't find that animal")} /> : <Page><ErrorNote error={error} retry={() => refetch()} /></Page>;
  if (a.merged_into) return <Page><Empty icon="info" title={t("This record was merged")} action={<Button to={`/cattle/${a.merged_into}`}>{t("Open the animal it was merged into")}</Button>} /></Page>;

  const sections = animalSections.filter((s) => { try { return s.show(a); } catch { return false; } });
  const actions = animalActions.filter((x) => { try { return !x.when || x.when(a); } catch { return false; } });
  const types = eventTypesFor("animal");
  const recordType = (type: string) => {
    const def = types.find((e) => e.type === type);
    const role = def?.subjects.find((s) => s.kind === "animal")?.role ?? "animal";
    openRecord({ initial: { type, subjects: [{ kind: "animal", id: a.id, role, label: a.label }] } });
  };
  const runAction = (x: (typeof actions)[number]) => {
    if (x.run) return x.run(a, navigate);
    if (!x.eventType) return;
    const def = types.find((e) => e.type === x.eventType);
    const role = def?.subjects.find((s) => s.kind === "animal")?.role ?? "animal";
    openRecord({ initial: { type: x.eventType, subjects: x.subjects?.(a) ?? [{ kind: "animal", id: a.id, role, label: a.label }], data: x.data?.(a) } });
  };
  const former = a.status !== "active" && a.status !== "missing";
  const setTab = (next: string) => setParams(next === "overview" ? {} : { tab: next }, { replace: true });
  const short = a.tag ?? a.name ?? a.label;
  const addMenu = <Menu label={t("Add a record")} variant="secondary" items={types.map((e) => ({ label: t(e.label), icon: e.icon, onSelect: () => recordType(e.type) }))}>{t("Add a record")}</Menu>;
  const moreMenu = <Menu label={t("More")} items={[
    { label: t("Edit details"), icon: "edit", onSelect: () => openEntity({ kind: "animal", id: a.id, initial: { tag: a.tag ?? undefined, name: a.name ?? undefined, sex: a.sex, birth_date: a.birth_date ?? undefined, breed: a.breed ?? undefined, color: a.color ?? undefined } }) },
    { label: t("Family"), icon: "family", onSelect: () => setTab("family") },
  ]} />;

  return (
    <Page>
      <PageHeader
        back={{ to: "/cattle", label: t("Cattle") }}
        title={<span className="animal-title">{a.tag ? <TagLabel tag={a.tag} size="xl" /> : null}{a.name && <span className="animal-title-name">{a.name}</span>}{!a.tag && !a.name && a.label}</span>}
        subtitle={[classLabel(a.class), a.age_days !== null ? formatAge(a.age_days) : null, a.breed, a.color].filter(Boolean).join(" · ")}
        actions={can("record") && (wide
          ? <><Button variant="primary" icon="mic" onClick={() => openComposer()}>{t("Tell Hondo about {{animal}}", { animal: short })}</Button>{addMenu}{moreMenu}</>
          : moreMenu)}
      >
        <div className="animal-status">
          {former && <Chip tone="neutral" icon="archive">{t(a.status === "reference" ? "Reference only" : a.status === "sold" ? "Sold" : a.status === "dead" ? "Dead" : a.status === "culled" ? "Culled" : "Transferred")}</Chip>}
          {a.status === "missing" && <Chip tone="warn" icon="warn">{t("Missing")}</Chip>}
          {a.location && <Link to={`/ranch/places/${a.location.id}`} className="chip tone-land"><Icon name="place" size={14} /><span>{a.location.label}</span></Link>}
          {a.groups.map((g) => <Link key={g.id} to={`/cattle/groups/${g.id}`} className="chip tone-neutral"><Icon name="group" size={14} /><span>{g.label}</span></Link>)}
          <Badges badges={a.badges} max={6} />
        </div>
      </PageHeader>

      <Tabs value={tab} onChange={setTab} tabs={[
        { id: "overview", label: t("Overview") },
        { id: "history", label: t("History"), count: a.counts.events },
        { id: "records", label: t("Records") },
        { id: "family", label: t("Family"), count: a.offspring.length },
      ]} />

      {tab === "overview" && (
        <div className="stack">
          {a.facts.length > 0 && <Card><Facts facts={a.facts} /></Card>}
          {can("record") && actions.length > 0 && (
            <div className="quick-actions" role="group" aria-label={t("Quick actions")}>{actions.slice(0, 6).map((x) => <Button key={x.id} icon={x.icon} onClick={() => runAction(x)}>{t(x.label)}</Button>)}</div>
          )}
          {(a.open_tasks.length > 0 || a.jobs.length > 0) && (
            <Section title={t("Next up")}>
              <List>
                {a.open_tasks.map((task) => <TaskRow key={task.id} task={task} today={today} />)}
                {a.jobs.map((j) => <Row key={j.id} icon="work" to={`/work/jobs/${j.id}`} title={j.title} meta={t("In this job")} />)}
              </List>
            </Section>
          )}
          <Section title={t("Recently")} action={<button type="button" className="link" onClick={() => setTab("history")}>{t("All history")}</button>}>
            <Timeline subject={`animal:${a.id}`} filters={false} limit={6} empty={t("Nothing recorded for {{animal}} yet", { animal: short })} />
          </Section>
        </div>
      )}

      {tab === "history" && <Timeline subject={`animal:${a.id}`} empty={t("Nothing recorded for {{animal}} yet", { animal: short })} />}

      {tab === "records" && <RecordsTab a={a} sections={sections} types={types} onAdd={recordType} />}

      {tab === "family" && <Family a={a} />}

      {can("record") && !wide && (
        <div className="sticky-actions" role="group" aria-label={t("Actions for {{animal}}", { animal: short })}>
          <Button variant="primary" icon="mic" onClick={() => openComposer()} aria-label={t("Tell Hondo about {{animal}}", { animal: short })}>{t("Tell Hondo")}</Button>
          <Menu label={t("Add a record")} variant="secondary" items={types.map((e) => ({ label: t(e.label), icon: e.icon, onSelect: () => recordType(e.type) }))}>{t("Add")}</Menu>
        </div>
      )}
    </Page>
  );
}

function RecordsTab({ a, sections, types, onAdd }: { a: AnimalDetail; sections: typeof animalSections; types: EventTypeDef[]; onAdd: (type: string) => void }) {
  const can = useCan();
  // "Add a record" offers what isn't shown yet, grouped by kind of work.
  const byModule = registry.modules.map((m) => ({ id: m.id, label: m.label, types: types.filter((e) => e.type.startsWith(`${m.id}.`)) })).filter((m) => m.types.length);
  const jump = (id: string) => document.getElementById(`records-${id}`)?.scrollIntoView({ block: "start" });
  return (
    <div className="stack records-tab">
      {sections.length > 0 && (
        <nav className="jump-chips" aria-label={t("Jump to")}>
          {sections.map((s) => <button key={s.id} type="button" className="choice" onClick={() => jump(s.id)}>{t(s.label)}</button>)}
          <button type="button" className="choice" onClick={() => jump("identity")}>{t("Identity and ownership")}</button>
        </nav>
      )}
      {sections.map((s) => (
        <section key={s.id} id={`records-${s.id}`} className="record-group">
          <h2>{t(s.label)}</h2>
          <Suspense fallback={<Spinner />}><s.Component animal={a} /></Suspense>
        </section>
      ))}
      <IdentitySection a={a} />
      {sections.length === 0 && <p className="muted">{t("Breeding, health, weights, genetics and sales show up here once something is recorded.")}</p>}
      {can("record") && byModule.length > 0 && (
        <section className="record-group add-records" aria-labelledby="add-record-title">
          <h2 id="add-record-title">{t("Add a record")}</h2>
          <div className="add-grid">
            {byModule.map((m) => (
              <div key={m.id} className="add-group">
                <h3>{t(m.label)}</h3>
                <div className="add-list">{m.types.map((e) => <button key={e.type} type="button" className="add-item" onClick={() => onAdd(e.type)}><Icon name={e.icon} size={16} />{t(e.label)}</button>)}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function IdentitySection({ a }: { a: AnimalDetail }) {
  const fmt = useFormat();
  const retained = Object.entries(a.attrs).filter(([k]) => !k.includes("."));
  return (
    <section id="records-identity" className="record-group">
      <h2>{t("Identity and ownership")}</h2>
      <Card>
        <dl className="data-list">
          {a.identifiers.map((i) => (
            <div key={i.id} className={i.valid_to ? "is-struck" : undefined}>
              <dt>{t(i.scheme === "visual" ? "Tag" : i.scheme === "eid" ? "EID" : i.scheme === "registration" ? "Registration" : i.scheme === "tattoo" ? "Tattoo" : i.scheme === "brand" ? "Brand" : "ID")}</dt>
              <dd className="mono">{i.value}{i.issuer ? ` · ${i.issuer}` : ""}{i.valid_to ? ` · ${t("retired {{date}}", { date: formatDate(i.valid_to) })}` : ""}</dd>
            </div>
          ))}
          <div><dt>{t("Sex")}</dt><dd>{t(a.sex === "female" ? "Female" : a.sex === "male" ? (a.castrated ? "Male, castrated" : "Male") : "Unknown sex")}</dd></div>
          {a.birth_date && <div><dt>{t("Born")}</dt><dd>{formatDate(a.birth_date, a.birth_precision === "estimated_date" ? "estimated_date" : "date")}</dd></div>}
          {a.origin && <div><dt>{t("Origin")}</dt><dd>{t(a.origin === "born" ? "Born here" : a.origin === "purchased" ? "Purchased" : a.origin === "received" ? "Received" : a.origin === "imported" ? "Imported" : "Unknown")}</dd></div>}
          {a.notes && <div><dt>{t("Notes")}</dt><dd>{a.notes}</dd></div>}
        </dl>
        {retained.length > 0 && (
          <details className="retained">
            <summary>{t("{{count}} imported fields kept as written", { count: retained.length })}</summary>
            <dl className="data-list">{retained.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{String(v)}</dd></div>)}</dl>
          </details>
        )}
        <DataList fields={Object.fromEntries(Object.entries(ENTITY_FIELDS.animal).filter(([k]) => k === "class_override"))} data={a as never} fmt={fmt} />
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------- family

interface PedigreeNode { id: string; label: string; dam_id: string | null; sire_id: string | null; status: string; sex: string; birth_date: string | null; breed: string | null }

function TreeNode({ node, role, self, from }: { node: PedigreeNode | null; role: string; self?: boolean; from?: unknown }) {
  if (!node) return <div className="tree-node is-unknown"><span className="tree-role">{role}</span><span className="muted">{t("Not recorded")}</span></div>;
  const body = (
    <>
      <span className="tree-role">{role}</span>
      <span className="tree-name">{node.label}</span>
      <span className="tree-meta">{[node.birth_date?.slice(0, 4), node.breed, node.status === "reference" ? t("Reference") : node.status !== "active" ? t(node.status === "sold" ? "Sold" : node.status === "dead" ? "Dead" : node.status === "culled" ? "Culled" : "Former") : null].filter(Boolean).join(" · ")}</span>
    </>
  );
  return self ? <div className="tree-node is-self">{body}</div> : <Link to={`/cattle/${node.id}`} state={from} className="tree-node">{body}</Link>;
}

/** Parents and grandparents. Genetics may add a deeper, richer pedigree as its own section. */
export function FamilyTree({ animalId }: { animalId: string }) {
  const q = useApi<{ root: string; nodes: Record<string, PedigreeNode> }>(`/animals/${animalId}/pedigree?depth=2`);
  const from = useFromHere(q.data?.nodes[animalId]?.label ?? t("Family"));
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const n = q.data.nodes;
  const self = n[animalId];
  const of = (id: string | null | undefined) => (id ? n[id] ?? null : null);
  const sire = of(self?.sire_id);
  const dam = of(self?.dam_id);
  return (
    <div className="tree" role="group" aria-label={t("Parents and grandparents")}>
      <div className="tree-self"><TreeNode node={self ?? null} role={t("This animal")} self /></div>
      <div className="tree-sire"><TreeNode node={sire} role={t("Sire")} from={from} /></div>
      <div className="tree-dam"><TreeNode node={dam} role={t("Dam")} from={from} /></div>
      <div className="tree-ss"><TreeNode node={of(sire?.sire_id)} role={t("Sire's sire")} from={from} /></div>
      <div className="tree-sd"><TreeNode node={of(sire?.dam_id)} role={t("Sire's dam")} from={from} /></div>
      <div className="tree-ds"><TreeNode node={of(dam?.sire_id)} role={t("Dam's sire")} from={from} /></div>
      <div className="tree-dd"><TreeNode node={of(dam?.dam_id)} role={t("Dam's dam")} from={from} /></div>
    </div>
  );
}

function Family({ a }: { a: AnimalDetail }) {
  const [showAll, setShowAll] = useState(false);
  const from = useFromHere(a.tag ?? a.label);
  const kids = showAll ? a.offspring : a.offspring.slice(0, 12);
  return (
    <div className="stack">
      {a.dam || a.sire ? <FamilyTree animalId={a.id} /> : (
        <div className={cx("parents")}>
          <Card><span className="eyebrow">{t("Dam")}</span><span className="parent-name">{t("Not recorded")}</span></Card>
          <Card><span className="eyebrow">{t("Sire")}</span><span className="parent-name">{t("Not recorded")}</span></Card>
        </div>
      )}
      <Section title={t("Offspring")} count={a.offspring.length}>
        {a.offspring.length === 0 ? <p className="muted">{a.sex === "male" ? t("No calves recorded with him as sire.") : t("No calves recorded.")}</p> : (
          <List>{kids.map((o) => <Row key={o.id} icon="calf" to={`/cattle/${o.id}`} state={from} title={o.label} meta={[o.birth_date ? formatDate(o.birth_date) : null, t(o.sex === "female" ? "Female" : o.sex === "male" ? "Male" : "Unknown sex"), o.status !== "active" ? t(o.status === "sold" ? "Sold" : o.status === "dead" ? "Dead" : o.status === "culled" ? "Culled" : o.status === "reference" ? "Reference only" : "Transferred") : null].filter(Boolean).join(" · ")} />)}</List>
        )}
        {a.offspring.length > 12 && !showAll && <Button variant="ghost" onClick={() => setShowAll(true)}>{t("Show all {{count}}", { count: a.offspring.length })}</Button>}
      </Section>
    </div>
  );
}
